import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Modal, Row, Col, Spinner } from "react-bootstrap";
import { User } from 'iconsax-react';
import MyApiClient from '../../../../../api-client/MyApiClient';
import { ruoloConsiglio } from '../../../../components/Consiglio/ruoli';
import { usePageTitle } from "../../../../layouts/PageHeaderExtra";
import { useStatoNelTitolo } from "../../../../components/Util/StatoProdotto";
import { segnalaMancanti } from "../../../../components/Util/campiMancanti";


// Le date arrivano come 'YYYY-MM-DD': si spezzano invece di passare da new Date(),
// che a ovest di Greenwich sposterebbe il giorno.
const formatDate = (valore) => {
	const iso = valore ? String(valore).substring(0, 10) : '';
	const [anno, mese, giorno] = iso.split('-');
	if (!anno || !mese || !giorno) return iso;
	return `${giorno}/${mese}/${anno}`;
};

export const SchedaSocio = () => {
	const navigate = useNavigate();

	const { id } = useParams();

	const [socio, setSocio] = useState(null);
	// Un socio in Archivio è un record chiuso (conservazione 10 anni): si consulta, non si modifica e non si rielimina
	const location = useLocation();
	const inArchivio = !!location.state?.inArchivio;
	const [confermaElimina, setConfermaElimina] = useState(false);
	// Titolo della pagina = nome del socio; finché non è caricato resta quello generico
	usePageTitle(socio?.name || '');
	const [loadingShowed, setLoadingShowed] = useState(false);

	const [modalShowed, setModalShowed] = useState(false);
	const [modalTitle, setModalTitle] = useState('');
	const [modalText, setModalText] = useState('');

	// Campi modificabili della scheda
	const [membershipRank, setMembershipRank] = useState('MEMBER');
	const [directorRole, setDirectorRole] = useState('COUNCILLOR');
	const [dateStart, setDateStart] = useState('');
	const [dateEnd, setDateEnd] = useState('');
	const [feePaid, setFeePaid] = useState('false');
	const [bookStatus, setBookStatus] = useState('ACTIVE');
	const [bookStatusDate, setBookStatusDate] = useState('');
	const [notes, setNotes] = useState('');

	// Stato nel libro soci nella tendina accanto al titolo: si salva subito (regola SUI).
	// La data dello stato, se non indicata, la mette il server (oggi); si corregge nel riquadro.
	const cambiaStato = async (nuovo) => {
		const auth = getAuth();
		if (!auth) return { annullato: true };
		const risultato = await new MyApiClient().updateMemberBookEntry(auth.token, id, { bookStatus: nuovo });
		if (!risultato || risultato.error) return { error: risultato?.error || 'Errore generico' };
		setBookStatus(risultato.bookStatus || nuovo);
		setBookStatusDate(risultato.bookStatusDate || '');
		if (risultato.dateEnd) setDateEnd(risultato.dateEnd);
		return {};
	};
	useStatoNelTitolo({
		visibile: !!socio,
		solaLettura: inArchivio ? 'Archiviato' : undefined,
		stati: [
			{ value: 'ACTIVE', label: 'Attivo', tono: 'attivo' },
			{ value: 'RESIGNED', label: 'Dimesso', tono: 'non-attivo' },
			{ value: 'EXPELLED', label: 'Espulso', tono: 'cancellato' },
		],
		valore: bookStatus,
		onCambia: cambiaStato,
	});

	const getAuth = () => {
		const tokenDetailsString = sessionStorage.getItem('userDetails');
		if (!tokenDetailsString) return null;
		const tokenDetails = JSON.parse(tokenDetailsString);
		return { token: tokenDetails.idToken, clubId: tokenDetails.localId };
	};

	const mostraErrore = (testo) => {
		setModalTitle("Errore durante l'operazione");
		setModalText(testo);
		setModalShowed(true);
	};

	useEffect(() => {
		const carica = async () => {
			const auth = getAuth();
			if (!auth) return;

			const apiClient = new MyApiClient();
			setLoadingShowed(true);
			const risultato = await apiClient.getMemberBookEntry(auth.token, id);
			setLoadingShowed(false);

			if (!risultato || risultato.error) {
				mostraErrore("Non è stato possibile caricare la scheda del socio.");
				return;
			}

			setSocio(risultato);
			setMembershipRank(risultato.membershipRank || 'MEMBER');
			setDirectorRole(risultato.directorRole || 'COUNCILLOR');
			setDateStart(risultato.dateStart || '');
			setDateEnd(risultato.dateEnd || '');
			setFeePaid(risultato.paymentStatus === 'PAID' ? 'true' : 'false');
			setBookStatus(risultato.bookStatus || 'ACTIVE');
			setBookStatusDate(risultato.bookStatusDate || '');
			setNotes(risultato.notes || '');
		};

		carica();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	const salva = async () => {
		const mancanti = [];
		if (!dateStart) mancanti.push({ campo: 'socio-data-entrata', messaggio: 'Manca la data di entrata' });
		if (bookStatus !== 'ACTIVE' && !bookStatusDate) {
			mancanti.push({ campo: 'socio-data-stato', messaggio: 'Con lo stato Dimesso o Espulso manca la data corrispondente' });
		}
		if (segnalaMancanti(mancanti) > 0) return;

		const auth = getAuth();
		if (!auth) return;

		const body = {
			dateStart: dateStart,
			bookStatus: bookStatus,
			notes: notes
		};
		if (dateEnd) body.dateEnd = dateEnd;
		if (bookStatusDate) body.bookStatusDate = bookStatusDate;
		// La quota si imposta a mano solo per i soci inseriti manualmente: per
		// quelli nati da un ordine lo stato deriva dall'ordine stesso.
		if (socio && socio.source !== 'ORDER') body.feePaid = (feePaid === 'true');

		const apiClient = new MyApiClient();
		setLoadingShowed(true);
		const risultato = await apiClient.updateMemberBookEntry(auth.token, id, body);
		setLoadingShowed(false);

		if (!risultato || risultato.error) {
			mostraErrore("Si è verificato un errore durante il salvataggio: " + (risultato ? risultato.error : 'errore generico'));
			return;
		}

		navigate('/struttura/soci/list');
	};

	const quotaDaOrdine = socio && socio.source === 'ORDER';

	// «Elimina socio» come «Elimina tessera»: entro 7 giorni dall'inserimento si cancella, dopo va in Archivio
	const elimina = async () => {
		setConfermaElimina(false);
		const auth = getAuth();
		if (!auth) return;
		setLoadingShowed(true);
		const risultato = await new MyApiClient().deleteMemberBookEntry(auth.token, id);
		setLoadingShowed(false);
		if (risultato && risultato.error) {
			mostraErrore("Si è verificato un errore durante l'eliminazione: " + risultato.error);
			return;
		}
		navigate('/struttura/soci/list');
	};

	return (
		<>
			<Modal className="fade" show={loadingShowed} centered>
				<Modal.Body className="text-center">
					<Spinner animation="border" role="status">
						<span className="sr-only">Loading...</span>
					</Spinner>
					<div>Caricamento in corso...</div>
				</Modal.Body>
			</Modal>

			<Modal className="fade" show={modalShowed} centered>
				<Modal.Header>
					<Modal.Title>{modalTitle}</Modal.Title>
					<Button onClick={() => setModalShowed(false)} variant="" className="btn-close"></Button>
				</Modal.Header>
				<Modal.Body>
					<Row><Col className="col-xl-64">{modalText}</Col></Row>
				</Modal.Body>
				<Modal.Footer className="justify-content-center">
					<Button className="btn btn-primary btn-add" onClick={() => setModalShowed(false)}>Chiudi</Button>
				</Modal.Footer>
			</Modal>

			<Modal className="fade" show={confermaElimina} onHide={() => setConfermaElimina(false)} centered>
				<Modal.Header closeButton><Modal.Title>Eliminare il socio?</Modal.Title></Modal.Header>
				<Modal.Body>
					Eliminare «{socio?.name}» dal libro soci?
					<br /><br />
					Se è stato inserito da meno di 7 giorni viene eliminato, perché si tratta di una correzione.
					Altrimenti esce dal libro soci (dimesso, con la data di oggi, se era attivo) e viene <b>archiviato</b>:
					per obbligo di conservazione decennale il record resta consultabile in Archivio.
				</Modal.Body>
				<Modal.Footer className="justify-content-center sid-footer-conferma">
					<Button className="btn btn-light btn-add" onClick={() => setConfermaElimina(false)}>Annulla</Button>
					<Button variant="danger" onClick={elimina}>Elimina</Button>
				</Modal.Footer>
			</Modal>

			<div style={{ maxWidth: '760px' }}>
			<fieldset disabled={inArchivio}>
				<div className="card">
					<div className="card-header d-flex justify-content-between align-items-center">
						<h4 className="card-title mb-0">Dati socio</h4>
						{socio && (
							<Button
								className="btn btn-light btn-add"
								onClick={() => { navigate('/struttura/clienti/player_profile/' + socio.userId); }}
							>
								<User size="18" className="me-1" /> Visualizza player
							</Button>
						)}
					</div>
					<div className="card-body">
						<Row>
							<Col md={6} className="mb-3">
								<label className="form-label">N. progressivo</label>
								<div className="form-control-plaintext">
									{socio && socio.memberNumber ? String(socio.memberNumber).padStart(3, '0') : '—'}
								</div>
							</Col>
							<Col md={6} className="mb-3">
								<label className="form-label">Player</label>
								<div className="form-control-plaintext">{socio ? socio.name : ''}</div>
							</Col>
							{socio && socio.decidedByAssembly && (
								<Col md={6} className="mb-3">
									<label className="form-label">Ammesso con delibera</label>
									{/* Il collegamento al verbale che ha deciso l'ammissione: senza,
									    la prova andrebbe cercata a mano fra le assemblee. Numero e
									    data sono quelli fotografati al momento della decisione. */}
									<div className="form-control-plaintext">
										<a href={'/struttura/assemblee/' + socio.decidedByAssembly.id}>
											{`Assemblea n. ${socio.decidedByAssembly.number}`}
											{socio.decidedByAssembly.date ? ` del ${formatDate(socio.decidedByAssembly.date)}` : ''}
										</a>
									</div>
								</Col>
							)}
							<Col md={6} className="mb-3">
								<label className="form-label">Livello</label>
								<div className="form-control-plaintext">
									{socio && socio.level === 'JUNIOR' ? 'Junior' : 'Senior'}
								</div>
							</Col>
							<Col md={6} className="mb-3">
								<label className="form-label">Tipologia</label>
								{/* Derivata, non modificabile: la nomina nel consiglio direttivo
								    avviene dalla sua pagina, che raccoglie anche mandato e firma.
								    Cambiarla da qui creava membri senza mandato. */}
								<div className="form-control-plaintext">
									{membershipRank === 'DIRECTOR'
										? `Consiglio direttivo${ruoloConsiglio(directorRole).label ? ' · ' + ruoloConsiglio(directorRole).label : ''}`
										: 'Socio'}
								</div>
								<div className="sid-firma-hint">
									La composizione del consiglio direttivo si gestisce dalla{' '}
									<a href="/struttura/cda/list" style={{ color: '#E66A0F', fontWeight: 600 }}>sua pagina</a>.
								</div>
							</Col>
							<Col md={6} className="mb-3">
								<label className="form-label" htmlFor="socio-data-entrata">Data in entrata *</label>
								<Form.Control id="socio-data-entrata" type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
							</Col>
							<Col md={6} className="mb-3">
								<label className="form-label">Data uscita</label>
								<Form.Control type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
							</Col>
						</Row>
					</div>
				</div>

				<div className="card">
					<div className="card-header"><h4 className="card-title mb-0">Stato pagamento</h4></div>
					<div className="card-body">
						<label className="form-label">Quota associativa</label>
						<Form.Select value={feePaid} onChange={(e) => setFeePaid(e.target.value)} disabled={quotaDaOrdine}>
							<option value="true">Pagato</option>
							<option value="false">Non pagato</option>
						</Form.Select>
						<small className="text-muted">
							{quotaDaOrdine
								? "Questo socio deriva da un acquisto: lo stato del pagamento segue l'ordine collegato e non è modificabile qui."
								: "Pagato = richiesta e pagamento ok · Non pagato = richiesta ok ma quota non pagata o scaduta."}
						</small>
					</div>
				</div>

				<div className="card">
					<div className="card-header"><h4 className="card-title mb-0">Stato nel libro soci</h4></div>
					<div className="card-body">
						<Row>
							<Col md={6} className="mb-3">
								<label className="form-label" htmlFor="socio-data-stato">Data stato</label>
								<Form.Control id="socio-data-stato" type="date" value={bookStatusDate} onChange={(e) => setBookStatusDate(e.target.value)} />
							</Col>
						</Row>
						<small className="text-muted">
							Lo stato (Attivo, Dimesso, Espulso) si sceglie dalla tendina accanto al nome e si salva subito, con la data di oggi: qui puoi correggere la data.
						</small>
					</div>
				</div>

				<div className="card">
					<div className="card-header"><h4 className="card-title mb-0">Note</h4></div>
					<div className="card-body">
						<Form.Control
							as="textarea"
							rows={4}
							value={notes}
							onChange={(e) => setNotes(e.target.value)}
							placeholder="Aggiungi note libere sul socio…"
						/>
					</div>
				</div>

			</fieldset>

				{/* Tasti della scheda (regola SUI): Elimina a sinistra, Annulla e Salva a destra. In Archivio solo il ritorno. */}
				<div className="d-flex justify-content-between mt-4 mb-4">
					{socio && !inArchivio
						? <button type="button" className="btn btn-danger light" onClick={() => setConfermaElimina(true)}>Elimina socio</button>
						: <div />}
					<div className="d-flex gap-4">
						{/* «Vendi la quota associativa»: era un'icona sulla riga della lista, ora sta qui (03/10/2026) */}
						{socio && !inArchivio && !socio.orderId && !socio.dateStart && (
							<Button
								className="btn btn-add sid-btn-secondary"
								onClick={() => {
									const parametri = new URLSearchParams({ newCustomerId: socio.userId, newCustomerName: socio.name || 'Cliente' });
									navigate('/struttura/booking/userSubscriptions/new?' + parametri.toString());
								}}
							>
								Vendi quota
							</Button>
						)}
						<Button className="btn btn-add sid-btn-secondary" onClick={() => { navigate('/struttura/soci/list'); }}>
							{inArchivio ? 'Chiudi' : 'Annulla'}
						</Button>
						{!inArchivio && <Button className="btn btn-primary btn-add" style={{ textTransform: 'none' }} onClick={salva}>Salva modifiche</Button>}
					</div>
				</div>
			</div>
		</>
	)
}

export default SchedaSocio;
